/* The zone list.
 *
 * Verification state is the column that matters: an unverified zone is not
 * served, so a zone sitting at "pending" looks created and does nothing. It
 * is shown as a state with an action, never as a quiet badge.
 *
 * Zone names link to the detail screen (Task 4), which is where verification
 * and, later, records and dynamic hosts are managed.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { api, type DnsZone } from "../api";

function VerificationBadge({ zone }: { zone: DnsZone }) {
  if (zone.verification_state === "verified") return <span className="ok">Verified</span>;
  if (zone.verification_state === "failed") return <span className="bad">Failed</span>;
  return <span className="warn">Not verified — not served</span>;
}

export function DnsZones() {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");

  const zones = useQuery({
    queryKey: ["dns", "zones"],
    queryFn: () => api.get<DnsZone[]>("/api/dns/zones"),
  });

  const createZone = useMutation({
    mutationFn: (zoneName: string) =>
      api.post<DnsZone>("/api/dns/zones", { name: zoneName.trim().toLowerCase() }),
    onSuccess: () => {
      setName("");
      void queryClient.invalidateQueries({ queryKey: ["dns", "zones"] });
    },
  });

  const deleteZone = useMutation({
    mutationFn: (id: string) => api.del(`/api/dns/zones/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["dns", "zones"] }),
  });

  return (
    <section>
      <div className="page-head">
        <div>
          <p className="label">Domains</p>
          <h1>DNS zones</h1>
        </div>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) createZone.mutate(name);
        }}
      >
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="example.co.za"
          aria-label="Zone name"
        />
        <button type="submit" disabled={createZone.isPending || !name.trim()}>
          Add zone
        </button>
      </form>
      {createZone.isError && <p className="bad">{(createZone.error as Error).message}</p>}

      {zones.isLoading && <p className="muted">Loading…</p>}
      {zones.isError && <p className="bad">{(zones.error as Error).message}</p>}

      <table>
        <thead>
          <tr>
            <th>Zone</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(zones.data ?? []).map((zone) => (
            <tr key={zone.id}>
              <td>
                <Link to={`/dns/${zone.id}`}>{zone.name}</Link>
              </td>
              <td>
                <VerificationBadge zone={zone} />
              </td>
              <td>
                <button
                  onClick={() => {
                    if (
                      window.confirm(
                        `Delete ${zone.name} and all of its records? This cannot be undone.`,
                      )
                    ) {
                      deleteZone.mutate(zone.id);
                    }
                  }}
                  disabled={deleteZone.isPending}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {zones.data?.length === 0 && <p className="muted">No zones yet.</p>}
    </section>
  );
}
